import type { VisualDiffResult } from '../types'
import { compareImages } from './visual-diff-service'

export interface MeasuredPixelEvidenceReceipt {
  schemaVersion: 'cogo.studio-scenario-pixel-evidence-receipt.v4'
  projectId: string
  projectRevision: number
  scenarioExecutionId: string
  evidenceRef: string
  authorityBaselineRef: string
  sourceImageHash: string
  currentImageHash: string
  viewport: {
    width: number
    height: number
    deviceScaleFactor: number
  }
  interactionState: {
    scenarioId: string
    stepId: string
    assertionIndex: number
    route: string
  }
  metricVersion: 'pixelmatch-diff-percent.v1'
  thresholdRef: string
  maxDiffPercent: number
  measuredDiffPercent: number
  verdict: 'pass' | 'fail'
}

export interface MeasuredPixelThresholdPolicy {
  evidenceRef: string
  thresholdRef: string
  maxDiffPercent: number
  interactionState: {
    scenarioId: string
    stepId: string
    assertionIndex: number
    route: string
  }
}

export interface MeasureScenarioPixelEvidenceInput {
  projectId: string
  projectRevision: number
  scenarioExecutionId: string
  currentImageDataUrl: string
  sourceImageDataUrl: string
  authorityBaseline: {
    authorityBaselineRef: string
    expectedSourceImageHash: string
  }
  viewport: MeasuredPixelEvidenceReceipt['viewport']
  policy: MeasuredPixelThresholdPolicy
}

interface MeasuredPixelEvidenceDependencies {
  compareImages: typeof compareImages
  hashImage(dataUrl: string): Promise<string>
}

const WRITER_OUTPUT = new WeakSet<object>()
const RECEIPT_KEYS = new Set([
  'schemaVersion',
  'projectId',
  'projectRevision',
  'scenarioExecutionId',
  'evidenceRef',
  'authorityBaselineRef',
  'sourceImageHash',
  'currentImageHash',
  'viewport',
  'interactionState',
  'metricVersion',
  'thresholdRef',
  'maxDiffPercent',
  'measuredDiffPercent',
  'verdict',
])
const HASH_PATTERN = /^sha256:[0-9a-f]{64}$/
const THRESHOLD_PATTERN = /^pixel-diff-percent-max:v1:(0|[1-9]\d{0,2})(?:\.(\d{1,4}))?$/

const DEFAULT_DEPENDENCIES: MeasuredPixelEvidenceDependencies = {
  compareImages,
  hashImage: hashImageDataUrl,
}

export async function measureScenarioPixelEvidence(
  input: MeasureScenarioPixelEvidenceInput,
  dependencies: MeasuredPixelEvidenceDependencies = DEFAULT_DEPENDENCIES,
): Promise<{
  result: VisualDiffResult
  receipt: MeasuredPixelEvidenceReceipt
}> {
  validateMeasurementInput(input)
  const result = await dependencies.compareImages(
    input.currentImageDataUrl,
    input.sourceImageDataUrl,
  )
  if (
    result.width !== input.viewport.width ||
    result.height !== input.viewport.height ||
    !Number.isFinite(result.diffPercent) ||
    result.diffPercent < 0 ||
    result.diffPercent > 100
  ) {
    throw new Error('Measured pixel result does not match the declared viewport')
  }
  const [sourceImageHash, currentImageHash] = await Promise.all([
    dependencies.hashImage(result.referenceImageDataUrl),
    dependencies.hashImage(result.currentImageDataUrl),
  ])
  if (!HASH_PATTERN.test(sourceImageHash) || !HASH_PATTERN.test(currentImageHash)) {
    throw new Error('Measured pixel image hashes must be canonical SHA-256 references')
  }
  if (sourceImageHash !== input.authorityBaseline.expectedSourceImageHash) {
    throw new Error('Measured pixel source image does not match the authority baseline hash')
  }

  const receipt: MeasuredPixelEvidenceReceipt = {
    schemaVersion: 'cogo.studio-scenario-pixel-evidence-receipt.v4',
    projectId: input.projectId,
    projectRevision: input.projectRevision,
    scenarioExecutionId: input.scenarioExecutionId,
    evidenceRef: input.policy.evidenceRef,
    authorityBaselineRef: input.authorityBaseline.authorityBaselineRef,
    sourceImageHash,
    currentImageHash,
    viewport: Object.freeze({ ...input.viewport }),
    interactionState: Object.freeze({ ...input.policy.interactionState }),
    metricVersion: 'pixelmatch-diff-percent.v1',
    thresholdRef: input.policy.thresholdRef,
    maxDiffPercent: input.policy.maxDiffPercent,
    measuredDiffPercent: result.diffPercent,
    verdict: result.diffPercent <= input.policy.maxDiffPercent ? 'pass' : 'fail',
  }
  Object.freeze(receipt)
  WRITER_OUTPUT.add(receipt)

  return { result, receipt }
}

export function isWriterProducedPixelEvidenceReceipt(
  value: unknown,
): value is MeasuredPixelEvidenceReceipt {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  if (!WRITER_OUTPUT.has(value)) return false
  const receipt = value as Record<string, unknown>
  return (
    Object.keys(receipt).length === RECEIPT_KEYS.size &&
    Object.keys(receipt).every((key) => RECEIPT_KEYS.has(key)) &&
    receipt.schemaVersion === 'cogo.studio-scenario-pixel-evidence-receipt.v4' &&
    typeof receipt.projectId === 'string' &&
    Number.isInteger(receipt.projectRevision) &&
    typeof receipt.scenarioExecutionId === 'string' &&
    receipt.scenarioExecutionId.length > 0 &&
    typeof receipt.evidenceRef === 'string' &&
    typeof receipt.authorityBaselineRef === 'string' &&
    receipt.authorityBaselineRef.length > 0 &&
    typeof receipt.sourceImageHash === 'string' &&
    HASH_PATTERN.test(receipt.sourceImageHash) &&
    typeof receipt.currentImageHash === 'string' &&
    HASH_PATTERN.test(receipt.currentImageHash) &&
    receipt.metricVersion === 'pixelmatch-diff-percent.v1' &&
    typeof receipt.thresholdRef === 'string' &&
    typeof receipt.maxDiffPercent === 'number' &&
    typeof receipt.measuredDiffPercent === 'number' &&
    (receipt.verdict === 'pass' || receipt.verdict === 'fail') &&
    isClosedViewport(receipt.viewport) &&
    isClosedInteractionState(receipt.interactionState)
  )
}

export function parseMeasuredPixelThresholdRef(thresholdRef: string): number | null {
  const match = THRESHOLD_PATTERN.exec(thresholdRef)
  if (!match) return null
  const threshold = Number(thresholdRef.slice(thresholdRef.lastIndexOf(':') + 1))
  return Number.isFinite(threshold) && threshold >= 0 && threshold <= 100 ? threshold : null
}

function validateMeasurementInput(input: MeasureScenarioPixelEvidenceInput): void {
  if (
    !input.projectId ||
    !Number.isInteger(input.projectRevision) ||
    input.projectRevision < 0 ||
    !input.scenarioExecutionId ||
    !input.currentImageDataUrl.startsWith('data:image/') ||
    !input.sourceImageDataUrl.startsWith('data:image/') ||
    !isAuthorityBaseline(input.authorityBaseline) ||
    !isClosedViewport(input.viewport) ||
    !isValidPolicy(input.policy)
  ) {
    throw new Error('Measured pixel evidence input is invalid')
  }
}

function isAuthorityBaseline(
  value: unknown,
): value is MeasureScenarioPixelEvidenceInput['authorityBaseline'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const baseline = value as Record<string, unknown>
  return (
    Object.keys(baseline).length === 2 &&
    Object.keys(baseline).every(
      (key) => key === 'authorityBaselineRef' || key === 'expectedSourceImageHash',
    ) &&
    typeof baseline.authorityBaselineRef === 'string' &&
    baseline.authorityBaselineRef.length > 0 &&
    typeof baseline.expectedSourceImageHash === 'string' &&
    HASH_PATTERN.test(baseline.expectedSourceImageHash)
  )
}

function isValidPolicy(policy: MeasuredPixelThresholdPolicy): boolean {
  return Boolean(
    policy &&
    policy.evidenceRef &&
    parseMeasuredPixelThresholdRef(policy.thresholdRef) === policy.maxDiffPercent &&
    isClosedInteractionState(policy.interactionState),
  )
}

function isClosedViewport(value: unknown): value is MeasuredPixelEvidenceReceipt['viewport'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const viewport = value as Record<string, unknown>
  return (
    Object.keys(viewport).length === 3 &&
    Object.keys(viewport).every(
      (key) => key === 'width' || key === 'height' || key === 'deviceScaleFactor',
    ) &&
    Number.isInteger(viewport.width) &&
    (viewport.width as number) > 0 &&
    Number.isInteger(viewport.height) &&
    (viewport.height as number) > 0 &&
    typeof viewport.deviceScaleFactor === 'number' &&
    Number.isFinite(viewport.deviceScaleFactor) &&
    viewport.deviceScaleFactor > 0
  )
}

function isClosedInteractionState(
  value: unknown,
): value is MeasuredPixelEvidenceReceipt['interactionState'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const state = value as Record<string, unknown>
  return (
    Object.keys(state).length === 4 &&
    Object.keys(state).every(
      (key) =>
        key === 'scenarioId' || key === 'stepId' || key === 'assertionIndex' || key === 'route',
    ) &&
    typeof state.scenarioId === 'string' &&
    state.scenarioId.length > 0 &&
    typeof state.stepId === 'string' &&
    state.stepId.length > 0 &&
    Number.isInteger(state.assertionIndex) &&
    (state.assertionIndex as number) >= 0 &&
    typeof state.route === 'string' &&
    state.route.length > 0
  )
}

async function hashImageDataUrl(dataUrl: string): Promise<string> {
  const comma = dataUrl.indexOf(',')
  if (comma < 0) throw new Error('Measured image must be a data URL')
  const metadata = dataUrl.slice(0, comma)
  const payload = dataUrl.slice(comma + 1)
  const bytes = metadata.endsWith(';base64')
    ? Uint8Array.from(atob(payload), (character) => character.charCodeAt(0))
    : new TextEncoder().encode(decodeURIComponent(payload))
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes)
  return `sha256:${Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')}`
}
